import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { SignIn, SignUp } from "@clerk/react";
import { useClerkReady } from "@/components/auth/ClerkAuthProvider";
import { Loader2, LockKeyhole } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { API, errMsg } from "@/lib/api";
import { markStaffSession } from "@/lib/staff";

const Shell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-slate-50">
    <div className="px-6 py-5"><Link to="/" data-testid="auth-home-link"><Logo /></Link></div>
    <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 pb-16">{children}</div>
  </div>
);

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-500";

// Bozza in memoria: sopravvive al rimontaggio quando l'accesso Google si carica in ritardo
const draft = { username: "", password: "" };

const StaffLogin = () => {
  const navigate = useNavigate();
  const [form, setFormState] = useState({ ...draft });
  const setForm = (next) => {
    Object.assign(draft, next);
    setFormState(next);
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/staff/login`, form, { withCredentials: true });
      markStaffSession();
      Object.assign(draft, { username: "", password: "" });
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    }
    setLoading(false);
  };
  return (
    <form onSubmit={submit} data-testid="staff-login-form" className="w-full max-w-[400px] rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
      <p className="flex items-center gap-2 text-lg font-semibold text-slate-900"><LockKeyhole className="h-5 w-5" /> Accesso Admin</p>
      <p className="mt-1 text-sm text-slate-500">Entra con utente e password dello staff.</p>
      <input data-testid="staff-username-input" className={`${field} mt-5`} placeholder="Utente" autoComplete="username"
        value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
      <input data-testid="staff-password-input" className={`${field} mt-3`} placeholder="Password" type="password" autoComplete="current-password"
        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      {error && <p data-testid="staff-login-error" className="mt-3 text-sm text-rose-600">{error}</p>}
      <button data-testid="staff-login-submit" disabled={loading || !form.username || !form.password}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white hover:bg-emerald-500 hover:text-slate-900 disabled:opacity-60">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Accedi
      </button>
    </form>
  );
};

export default function SignInPage() {
  const clerkReady = useClerkReady();
  return (
    <Shell>
      <StaffLogin />
      {clerkReady && (
        <>
          <p className="text-xs uppercase tracking-widest text-slate-400">oppure con Google</p>
          <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp fallbackRedirectUrl="/admin" />
        </>
      )}
    </Shell>
  );
}

export function SignUpPage() {
  const clerkReady = useClerkReady();
  return (
    <Shell>
      {clerkReady ? <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" fallbackRedirectUrl="/admin" /> : <StaffLogin />}
    </Shell>
  );
}
